import React, { FC, useEffect } from 'react';
import VideoJsPlayer from 'video.js';
import { select } from '~/lib/select';
import {
  notifyLearnerVideoActivity,
  VideoActivityType
} from '~/shell/PostMessage';
import { TrainingState, useTrainingStore } from '~/training/state';
import { MediaKind, MediaPlayerStore } from './MediaPlayerStore';

import { createContext, useContext, useRef } from 'react';
import { useStore, create, StateSelector, StoreApi } from 'zustand';

const fixSrc = (
  src: string //src;
) =>
  window.location.hostname === 'localhost' && window.location.port === '4000'
    ? src.replace('https://static.easyllama.com', '/easyllama')
    : src;

function isJSON(src: string) {
  try {
    JSON.parse(src);
  } catch (e) {
    return false;
  }
  return true;
}

const notifyVideoActivity = (activityType: VideoActivityType) => {
  const { learnerTrainingId, items, activeItemPosition } =
    useTrainingStore.getState();

  if (!learnerTrainingId) {
    return;
  }

  const sequence = items.find(
    i => i.kind === 'sequence' && i.position === activeItemPosition
  );

  notifyLearnerVideoActivity(
    learnerTrainingId,
    activityType,
    sequence?.id ?? ''
  );
};

export const createStore = () => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const players: { [key in MediaKind]?: VideoJsPlayer } = {};

  // This handler is defined here so it can be removed from the text track and prevent memory leaks
  const cueChangeHandler = () => {
    const player = store.getState().player();
    if (!player) return;

    // Force track mode for Firefox
    forceTrackMode(player);

    const tracks = player.textTracks();

    for (let i = 0; i < tracks.length; i++) {
      const t = tracks[i];
      // Find the first subtitles track
      if (t.kind !== 'subtitles') continue;

      // For some reason, this doesn't do anything when creating the text track but adding
      // it here does hide the default captions
      t.mode = 'hidden';

      const captions: string[] = [];

      if (t.activeCues && t.activeCues.length > 0) {
        for (let i = 0; i < t.activeCues.length; i++) {
          const cue = t.activeCues[i];
          if (cue && cue.text) {
            //it is hot fix
            const res = cue.text.replace(/"/gi, '');
            if (res) {
              if (!isJSON(res)) {
                captions.push(res);
              }
            }
          }
        }
      }

      store.setState({ captions });
      return;
    }
  };

  const forceTrackMode = (
    player: Parameters<MediaPlayerStore['setPlayer']>[0]
  ) => {
    const tracks = player.textTracks();
    for (let i = 0; i < tracks.length; i++) {
      const track = tracks[i];
      if (track.kind === 'subtitles' && track.mode !== 'hidden') {
        track.mode = 'hidden';
      }
    }
  };

  /**
   * Removes all text tracks from the player
   */
  const clearTextTracks = () => {
    const player = store.getState().player();
    if (!player) return;

    const remoteTracks = player.remoteTextTracks();

    for (let i = remoteTracks.length - 1; i >= 0; i--) {
      const track = remoteTracks[i];

      track.removeEventListener('cuechange', cueChangeHandler);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      player.removeRemoteTextTrack(track as any);
    }

    const videoElement = player.el().querySelector('video');

    if (videoElement) {
      const textTracks = videoElement.textTracks;
      for (let i = textTracks.length - 1; i >= 0; i--) {
        const track = textTracks[i];
        if (track.kind === 'subtitles') {
          track.mode = 'disabled';
        }
      }
    }
  };

  const store = create<MediaPlayerStore>((set, get) => ({
    status: 'nomedia',
    progress: 0,
    currentTime: 0,
    mediaSrc: '',
    mediaKind: 'none',
    subtitlesSrc: '',
    captions: [],
    playbackSpeed: 1,
    snapshotUrl: '',

    setPlayer: (player, kind) => {
      // just in case, pause any media already playing
      try {
        players[kind]?.pause();
      } catch (error) {
        // Silently ignore player operation errors
      }

      players[kind] = player;
      if (!player) return;

      player.on('error', function () {
        // Silently handle media errors - not critical for functionality
        set({ status: 'nomedia' });
      });

      player.on('abort', function () {
        // Silently handle media abort - not critical for functionality
        set({ status: 'nomedia' });
      });

      player.on('stalled', function () {
        // Silently handle media stall - not critical for functionality
        set({ status: 'nomedia' });
      });

      player.on('loadedmetadata', () => {
        if (get().status !== 'loading') return;
        set({ status: 'ready' });
      });

      player.on('canplay', () => {
        if (get().status !== 'loading') return;
        set({ status: 'ready' });
      });
      player.on('play', () => {
        set({ status: 'playing' });
        if (kind === 'video') {
          notifyVideoActivity('video_started');
        }
        // Force cueChangeHandler execution for Firefox
        setTimeout(() => {
          forceTrackMode(player);
          cueChangeHandler();
        }, 100);
      });
      player.on('pause', () => {
        if (get().status === 'playing') {
          set({ status: 'paused' });
          if (kind === 'video') {
            notifyVideoActivity('video_paused');
          }
        }
      });
      player.on('ended', () => {
        set({ status: 'done' });
        if (kind === 'video') {
          notifyVideoActivity('video_ended');
        }
      });
      player.on('timeupdate', () => {
        // Don't track the current time when not playing
        if (get().status !== 'playing') return;
        try {
          set({
            progress: player.currentTime() / player.duration(),
            currentTime: player.currentTime()
          });

          // Periodically check for captions in Firefox
          if (player.currentTime() % 1 < 0.1) {
            cueChangeHandler();
          }
        } catch (error) {
          // Silently ignore player operation errors
        }
      });
    },

    removePlayer: (player, kind) => {
      if (players[kind] !== player) return;
      delete players[kind];
    },

    player: () => players[get().mediaKind],

    play: async () => {
      // Note: this may not work if the user has not interacted with the page

      const playPromise = get().player()?.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            get().player()?.playbackRate(get().playbackSpeed);
          })
          .catch(() => {
            // Silently ignore autoplay restrictions and interruption errors
            // No logging needed - these are expected browser behaviors
          });
      }
    },

    pause: () => {
      try {
        get().player()?.pause();
      } catch (error) {
        // Silently ignore player operation errors
      }
    },

    toggle: () => {
      const { status, play, pause } = get();
      if (status === 'playing') pause();
      else if (status === 'paused' || status === 'ready') play();
    },

    reset: () => {
      const player = get().player();
      try {
        player?.pause();
      } catch (error) {
        // Silently ignore player operation errors
      }
      player?.currentTime(0);
      set({
        progress: 0,
        currentTime: 0,
        status: 'nomedia',
        mediaSrc: '',
        mediaKind: 'none',
        subtitlesSrc: '',
        captions: [],
        snapshotUrl: ''
      });
      clearTextTracks();
    },

    restart: () => {
      const player = get().player();
      player
        ?.play()
        ?.then(() => {
          player?.currentTime(0);
          player?.pause();
        })
        .catch(() => {
          // Silently ignore play interruption errors
        });
    },

    loadMedia: (
      mediaSrc,
      subtitlesSrc,
      kind,
      autoplay,
      playDelay = 0,
      snapshotUrl
    ) => {
      if (typeof mediaSrc === 'string') {
        mediaSrc = fixSrc(mediaSrc || '');
      } else if (mediaSrc) {
        mediaSrc = mediaSrc.map(({ src, type }) => ({
          src: fixSrc(src),
          type
        }));
      }
      // Do nothing if the media hasn't changed
      if (mediaSrc !== get().mediaSrc) {
        try {
          players.audio?.pause();
          players.video?.pause();
        } catch (error) {
          // Silently ignore player operation errors
        }
        const player = players[kind];

        if (!mediaSrc) {
          set({
            status: 'nomedia',
            mediaSrc: '',
            mediaKind: 'none',
            snapshotUrl: ''
          });
        } else {
          set({ status: 'loading', mediaSrc, mediaKind: kind, snapshotUrl });
          if (!player) {
            set({
              status: 'nomedia',
              mediaSrc: '',
              mediaKind: 'none',
              snapshotUrl: ''
            });
          }
          if (snapshotUrl) player?.poster(fixSrc(snapshotUrl));
          player?.src(
            typeof mediaSrc === 'string'
              ? {
                  src: mediaSrc,
                  type: kind === 'audio' ? 'audio/mpeg' : 'video/mp4'
                }
              : mediaSrc
          );
          player?.currentTime(get().currentTime);

          const urlParams = new URLSearchParams(window.location.search);
          const dontPlayAudioAndVideo = urlParams.has('dontPlayAudioAndVideo');

          if (autoplay && !dontPlayAudioAndVideo) {
            setTimeout(() => {
              get().play();
            }, playDelay);
          }
        }
      }
      get().loadSubtitles(subtitlesSrc);
      clearTextTracks();
    },

    loadSubtitles: src => {
      const url = fixSrc(src || '');
      set({ subtitlesSrc: url });

      clearTextTracks();

      const player = get().player();
      if (!url || !player) return;

      // Add a small delay for Firefox to ensure proper track handling
      setTimeout(() => {
        const currentPlayer = get().player();
        if (!currentPlayer) return;

        const track = currentPlayer.addRemoteTextTrack(
          {
            kind: 'subtitles',
            src: url,
            mode: 'hidden',
            default: true,
            srclang: 'en',
            label: 'English'
          },
          true
        );

        // Ensure the track is properly configured for Firefox
        if (track) {
          track.addEventListener('cuechange', cueChangeHandler);

          // Add additional event listeners for Firefox compatibility
          track.addEventListener('load', () => {
            // Force mode setting for Firefox compatibility
            setTimeout(() => {
              if (track.mode !== 'hidden') {
                track.mode = 'hidden';
              }
              // Trigger cueChangeHandler manually for Firefox
              setTimeout(() => {
                cueChangeHandler();
              }, 50);
            }, 100);
          });

          // Force initial cueChangeHandler call
          setTimeout(() => {
            cueChangeHandler();
          }, 200);
        }
      }, 50);
    },

    setSpeed: speed => {
      get().player()?.playbackRate(speed);
      set({ playbackSpeed: speed });
    },

    seek: span => {
      const player = get().player();
      if (!player) return;
      player.currentTime(player.currentTime() + span);
    }
  }));

  return store;
};

const StoreContext = createContext<StoreApi<MediaPlayerStore> | null>(null);

const selector = (s: TrainingState) =>
  select(s, 'playbackSpeed', 'featureFocus');
const mediaSelector = (s: MediaPlayerStore) => select(s, 'setSpeed', 'pause');

const MediaPlayerHelper = () => {
  const store = useContext(StoreContext);
  if (!store) {
    throw new Error('Missing StoreProvider');
  }

  const { playbackSpeed, featureFocus } = useTrainingStore(selector);
  const { setSpeed, pause } = useStore(store, mediaSelector);

  useEffect(() => {
    // Update playback speed when the settings change
    setSpeed(playbackSpeed);
  }, [playbackSpeed]);

  useEffect(() => {
    if (featureFocus) {
      // Auto pause playback when window loses focus
      window.addEventListener('blur', pause);
      return () => window.removeEventListener('blur', pause);
    }
  }, []);

  return null;
};

interface MediaPlayerProviderProps {
  children: React.ReactNode;
}

export const MediaPlayerProvider: FC<MediaPlayerProviderProps> = ({
  children
}) => {
  const storeRef = useRef<StoreApi<MediaPlayerStore> | null>(null);
  if (!storeRef.current) {
    storeRef.current = createStore();
  }
  return (
    <StoreContext.Provider
      value={storeRef.current as StoreApi<MediaPlayerStore>}
    >
      <MediaPlayerHelper />
      {children}
    </StoreContext.Provider>
  );
};

export const useMediaPlayerStore = <T = MediaPlayerStore,>(
  selector: StateSelector<MediaPlayerStore, T> = state => state as unknown as T,
  shallow?: (a: T, b: T) => boolean
): T => {
  const store = useContext(StoreContext);
  if (!store) {
    throw new Error('Missing StoreProvider');
  }

  if (shallow) {
    return useStore(store, selector, shallow) as T;
  }
  return useStore(store, selector) as T;
};
